// src/pages/AdminFormsPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Select, MenuItem, FormControl, InputLabel, TextField,
  IconButton, Paper, Chip, Divider, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../api/api';

const FIELD_TYPES = ['text', 'number', 'select', 'textarea', 'date', 'checkbox'];

const emptyField = (serviceId) => ({
  service_id: serviceId,
  field_key: '',
  label: '',
  field_type: 'text',
  required: true,
  options: '',
  position: 0,
});

export default function AdminFormsPage() {
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [newField, setNewField] = useState(null);

  const fetchAll = async (currentServiceId) => {
    setLoading(true);
    try {
      const { data } = await api.get('/form-fields/admin/all');
      // backend devuelve { services: [{ id, service_name, slug, fields }] }
      const list = data.services || [];
      setServices(list);
      const svc = list.find(s => s.id === currentServiceId);
      setFields(svc?.fields || []);
    } catch {
      setError('Error recargando campos');
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial
  useEffect(() => {
    api.get('/form-fields/admin/all')
      .then(({ data }) => {
        const list = data.services || [];
        setServices(list);
        if (list.length) {
          setSelectedServiceId(list[0].id);
          setFields(list[0].fields || []);
        }
      })
      .catch(() => setError('Error cargando formularios del backend'))
      .finally(() => setLoading(false));
  }, []);

  // Actualiza campos cuando cambia el servicio seleccionado
  useEffect(() => {
    if (!selectedServiceId || !services.length) return;
    const svc = services.find(s => s.id === selectedServiceId);
    setFields(svc?.fields || []);
    setEditingId(null);
    setNewField(null);
  }, [selectedServiceId, services]);

  const handleSaveEdit = async (fieldId) => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/form-fields/admin/${fieldId}`, editDraft);
      setSuccess('Campo actualizado correctamente');
      setEditingId(null);
      await fetchAll(selectedServiceId);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('¿Eliminar este campo del formulario?')) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/form-fields/admin/${fieldId}`);
      setSuccess('Campo eliminado');
      await fetchAll(selectedServiceId);
    } catch {
      setError('Error eliminando campo');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateField = async () => {
    if (!newField.label || !newField.field_key) {
      setError('Label y field_key son requeridos');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const selectedSvc = services.find(s => s.id === selectedServiceId);
      await api.post(`/form-fields/admin/${selectedSvc.slug}`, newField);
      setSuccess('Campo creado correctamente');
      setNewField(null);
      await fetchAll(selectedServiceId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creando campo');
    } finally {
      setSaving(false);
    }
  };

  const selectedSvcName = services.find(s => s.id === selectedServiceId)?.service_name || '';

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, px: 2, pb: 6 }}>
      <Typography variant="h5" fontWeight={700} color="#163244" mb={3}>
        Editor de Formularios Dinámicos
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Selector de servicio */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Servicio</InputLabel>
        <Select
          value={selectedServiceId}
          label="Servicio"
          onChange={e => setSelectedServiceId(e.target.value)}
        >
          {services.map(s => (
            <MenuItem key={s.id} value={s.id}>
              {s.service_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: '#163244' }} />
        </Box>
      ) : (
        <>
          {fields.length === 0 && !newField && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Este servicio no tiene campos configurados todavía.
            </Alert>
          )}

          {/* Lista de campos existentes */}
          {fields.map((field) => (
            <Paper key={field.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              {editingId === field.id ? (
                <FieldForm
                  draft={editDraft}
                  onChange={setEditDraft}
                  onSave={() => handleSaveEdit(field.id)}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{field.label}</Typography>
                    <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                      <Chip label={field.field_type} size="small" sx={{ bgcolor: '#e3eef5' }} />
                      <Chip label={`key: ${field.field_key}`} size="small" variant="outlined" />
                      <Chip label={`pos: ${field.position}`} size="small" variant="outlined" />
                      {field.required && (
                        <Chip label="requerido" size="small" color="warning" />
                      )}
                    </Stack>
                  </Box>
                  <IconButton
                    onClick={() => { setEditingId(field.id); setEditDraft({ ...field }); }}
                    title="Editar"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(field.id)}
                    title="Eliminar"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Paper>
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Crear nuevo campo */}
          {newField ? (
            <Paper
              elevation={2}
              sx={{ p: 2.5, borderRadius: 2, border: '2px dashed #E7A432' }}
            >
              <Typography fontWeight={700} color="#163244" mb={2}>
                Nuevo campo para "{selectedSvcName}"
              </Typography>
              <FieldForm
                draft={newField}
                onChange={setNewField}
                onSave={handleCreateField}
                onCancel={() => setNewField(null)}
                saving={saving}
              />
            </Paper>
          ) : (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setNewField(emptyField(selectedServiceId))}
              sx={{
                borderColor: '#163244', color: '#163244',
                fontWeight: 600, borderWidth: 2,
                '&:hover': { borderWidth: 2, bgcolor: '#f0f4f8' },
              }}
            >
              Agregar campo a "{selectedSvcName}"
            </Button>
          )}
        </>
      )}
    </Box>
  );
}

// Sub-componente reutilizable para editar/crear un campo
function FieldForm({ draft, onChange, onSave, onCancel, saving }) {
  const set = (key, val) => onChange(prev => ({ ...prev, [key]: val }));

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          size="small"
          label="Label (visible al usuario)"
          value={draft.label || ''}
          onChange={e => set('label', e.target.value)}
          fullWidth
          required
        />
        <TextField
          size="small"
          label="field_key (identificador)"
          value={draft.field_key || ''}
          onChange={e => set('field_key', e.target.value)}
          fullWidth
          required
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Tipo de campo</InputLabel>
          <Select
            value={draft.field_type || 'text'}
            label="Tipo de campo"
            onChange={e => set('field_type', e.target.value)}
          >
            {FIELD_TYPES.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Posición"
          type="number"
          value={draft.position ?? 0}
          onChange={e => set('position', Number(e.target.value))}
          sx={{ width: 110 }}
          inputProps={{ min: 0 }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Requerido</InputLabel>
          <Select
            value={draft.required ? 'true' : 'false'}
            label="Requerido"
            onChange={e => set('required', e.target.value === 'true')}
          >
            <MenuItem value="true">Sí</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {draft.field_type === 'select' && (
        <TextField
          size="small"
          label='Opciones (JSON array: ["Opción 1","Opción 2"])'
          value={draft.options || ''}
          onChange={e => set('options', e.target.value)}
          fullWidth
          helperText='Formato JSON: ["Op1", "Op2", "Op3"]'
        />
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          startIcon={<CancelIcon />}
          onClick={onCancel}
          color="inherit"
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          variant="contained"
          onClick={onSave}
          disabled={saving}
          sx={{ bgcolor: '#163244', '&:hover': { bgcolor: '#0f2333' } }}
        >
          Guardar
        </Button>
      </Stack>
    </Stack>
  );
}
